/**
 * useSyncSchedules ([D-356], 02-10-2026) — status for synk-kilderne til
 * tandhjulets synk-felter og oversigten på Indstillinger → Sideindstillinger.
 *
 * Datakilde: GET /api/page-settings/sync (kun kilder brugeren har adgang til
 * via en af kildens sider). Intervallet SELV hentes og gemmes som en
 * almindelig sideindstilling (usePageSettings); her er kun det der ikke kan
 * gemmes: seneste kørsel, typisk varighed og Sync-doktorens fund.
 *
 * Modul-scoped cache, så drawer og oversigt deler data; `load(true)` efter
 * et gem, så status og doktorens fund er friske.
 */

import { ref } from 'vue';
import type { SyncDoctorFindingDto, SyncSourceStatusDto } from '@cockpit/shared';

import { fetchJson } from '../lib/http';

const sources = ref<SyncSourceStatusDto[]>([]);
const findings = ref<SyncDoctorFindingDto[]>([]);
const loaded = ref(false);
const loading = ref(false);
const error = ref<string | null>(null);

export function useSyncSchedules() {
  async function load(force = false): Promise<void> {
    if (loading.value || (loaded.value && !force)) return;
    loading.value = true;
    try {
      const res = await fetchJson<{ sources: SyncSourceStatusDto[]; findings: SyncDoctorFindingDto[] }>(
        '/api/page-settings/sync',
      );
      sources.value = res.sources;
      findings.value = res.findings;
      loaded.value = true;
      error.value = null;
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
    } finally {
      loading.value = false;
    }
  }

  function statusFor(id: string | undefined): SyncSourceStatusDto | undefined {
    return id ? sources.value.find((s) => s.id === id) : undefined;
  }

  return { sources, findings, loaded, loading, error, load, statusFor };
}
